import React, { useEffect, useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Check, X, Loader2, PartyPopper, Sparkles } from 'lucide-react';

// Confetti CSS keyframes - added as a style tag
const confettiStyles = `
  @keyframes confetti-fall {
    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
  .animate-shake { animation: shake 0.5s ease-in-out; }
`;

// Generate particles once with useMemo to avoid re-renders
const generateParticles = () => {
  return Array.from({ length: 50 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 1 + Math.random() * 2,
    color: ['#10b981', '#8b5cf6', '#f59e0b', '#3b82f6', '#ec4899'][Math.floor(Math.random() * 5)]
  }));
};

// Success Animation with Confetti
export const SuccessAnimation = ({ 
  show, 
  message = "Success!", 
  subMessage,
  onComplete,
  duration = 3000 
}) => {
  const [visible, setVisible] = useState(false);
  const confetti = useMemo(() => generateParticles(), []);

  useEffect(() => {
    if (show && !visible) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, duration);
      return () => clearTimeout(timer);
    }
    if (!show && visible) {
      setVisible(false);
    }
  }, [show, duration, onComplete, visible]);

  if (!visible) return null;

  return (
    <>
      <style>{confettiStyles}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
        {/* Confetti */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confetti.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-3 h-3 rounded-sm"
              style={{
                left: `${particle.x}%`,
                top: '-10px',
                backgroundColor: particle.color,
                animation: `confetti-fall ${particle.duration}s ease-out ${particle.delay}s forwards`
              }}
            />
          ))}
        </div>
        
        {/* Success Card */}
        <div className="relative bg-slate-900 border border-emerald-500/50 rounded-2xl p-8 text-center shadow-[0_0_50px_rgba(16,185,129,0.3)] animate-in zoom-in-95 duration-300">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2">
            <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.5)] animate-bounce">
              <Check className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <div className="pt-8">
            <PartyPopper className="w-12 h-12 text-amber-400 mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-white mb-2">{message}</h2>
            {subMessage && <p className="text-slate-400">{subMessage}</p>}
          </div>
        </div>
      </div>
    </>
  );
};

// Error Animation with Shake
export const ErrorAnimation = ({ 
  show, 
  message = "Something went wrong", 
  subMessage,
  onComplete,
  duration = 3000 
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show && !visible) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, duration);
      return () => clearTimeout(timer);
    }
    if (!show && visible) {
      setVisible(false);
    }
  }, [show, duration, onComplete, visible]);

  if (!visible) return null;

  return (
    <>
      <style>{confettiStyles}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
        <div className="bg-slate-900 border border-red-500/50 rounded-2xl p-8 text-center shadow-[0_0_50px_rgba(239,68,68,0.2)] animate-shake">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{message}</h2>
          {subMessage && <p className="text-slate-400">{subMessage}</p>}
        </div>
      </div>
    </>
  );
};

// Loading Spinner with Text
export const LoadingSpinner = ({ 
  size = 'default', 
  text,
  className 
}) => {
  const sizes = {
    sm: 'w-4 h-4',
    default: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <Loader2 className={cn("animate-spin text-emerald-500", sizes[size])} />
      {text && <p className="text-slate-400 text-sm">{text}</p>}
    </div>
  );
};

// Pulse Dot - for status indicators
export const PulseDot = ({ color = 'emerald', className }) => {
  const colors = {
    emerald: 'bg-emerald-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    violet: 'bg-violet-500'
  };

  return (
    <span className={cn("relative flex h-3 w-3", className)}>
      <span className={cn(
        "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
        colors[color]
      )} />
      <span className={cn(
        "relative inline-flex rounded-full h-3 w-3",
        colors[color]
      )} />
    </span>
  );
};

// Fade In Animation Wrapper
export const FadeIn = ({ children, delay = 0, className }) => (
  <div
    className={cn("animate-in fade-in duration-500", className)}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

// Slide Up Animation Wrapper
export const SlideUp = ({ children, delay = 0, className }) => (
  <div
    className={cn("animate-in slide-in-from-bottom-4 fade-in duration-500", className)}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

// Scale In Animation Wrapper
export const ScaleIn = ({ children, delay = 0, className }) => (
  <div
    className={cn("animate-in zoom-in-95 fade-in duration-300", className)}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

// Sparkle Effect - decorative
export const SparkleEffect = ({ className }) => (
  <Sparkles className={cn("w-4 h-4 text-amber-400 animate-pulse", className)} />
);

// Counter Animation - animates number counting up
export const AnimatedCounter = ({ value, duration = 1000, prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setDisplayValue(Math.floor(progress * value));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return (
    <span className="tabular-nums">
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

// Progress Bar with Animation
export const AnimatedProgress = ({ value, max = 100, className, showLabel = true }) => {
  const percentage = Math.min((value / max) * 100, 100);

  return (
    <div className={cn("space-y-1", className)}>
      {showLabel && (
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Progress</span>
          <span className="text-emerald-400 font-medium">{Math.round(percentage)}%</span>
        </div>
      )}
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default SuccessAnimation;
